import * as STD             from '@emersya/mpa-std';
import * as CM              from '@emersya/mpa-components-manager';

export interface IMenuTabComponentConfiguration extends IMenuViewsComponentConfiguration {
	defaultSection?: CM.Component.TId;
};

type TView = SectionComponent & {
	component_outerChilds: [
		CM.DOMComponent<any, any, any>,
		CM.DOMComponent<any, any, any>
	];
};

/**
 ** Tabs expect this child tree :
 **	<section id='WHATEVER'>
 **		<input-button id='header'>
 **			...WHATEVER...
 **		</input-button>
 **		<block id='content'/>
 **	</section>
 **/
export class MenuTabComponent extends MenuViewsComponent<
	MenuTabComponent,
	IMenuTabComponentConfiguration
> {
	static override initialize() {
		MenuViewsComponent.initialize.call(this);
		this.declareEventType('menu-open');
		this.declareEventType('menu-close');
	};

	protected header!: CM.DOMComponent<any, any, any>;
	protected content!: CM.DOMComponent<any, any, any>;


	protected getView(viewId: string) {
		const view = this.component_outerChilds.find(x => x.id === viewId);
		if (!view) {
			this.component_throwUserError('View not found', {
				viewId
			});
		}
		if (
			!(view instanceof SectionComponent)
		) {
			this.component_throwUserError('viewId does not reference a view!', {
				viewId,
				matchedComponent: view
			});
		}
		return view as TView;
	};

	protected getViewHeader(viewId: string) {
		const viewHeader = this.header.component_outerChilds.find(x => x.id === viewId);
		if (!viewHeader) {
			this.component_throwUserError(`View<${viewId}> does not exists!`, {
				viewId
			});
		}
		return <CM.DOMComponent<any, any, any>>viewHeader;
	};

	protected getViewContent(viewId: string) {
		const viewContent = this.content.component_outerChilds.find(x => x.id === viewId);
		if (!viewContent) {
			this.component_throwUserError(`View<${viewId}> does not exists!`, {
				viewId
			});
		}
		return <CM.DOMComponent<any, any, any>>viewContent;
	};

	override async component_initialize() {
		await super.component_initialize();
		this.header = <CM.DOMComponent<any, any, any>>this.component_find('@:header');
		this.content = <CM.DOMComponent<any, any, any>>this.component_find('@:content');
		for (const c of this.component_outerChilds) {
			if (!(c instanceof SectionComponent)) {
				await this.header.component_appendOuterChild(c, this.header.component_DOM);
			} else {
				const itemHeader = <CM.DOMComponent<any, any, any>>c.component_outerChilds[0]!;
				const itemContent = <CM.DOMComponent<any, any, any>>c.component_outerChilds[1]!;
				const headerBlock = <BlockComponent>await this.header.component_instantiateOuterChild({
					type: 'block',
					id: c.id,
					tree: this.component_tree,
					configuration: {},
					slot: null,
					isSlot: false
				});
				await headerBlock.component_appendOuterChild(itemHeader, headerBlock.component_DOM);
				const contentBlock = <BlockComponent>await this.content.component_instantiateOuterChild({
					type: 'block',
					id: c.id,
					tree: this.component_tree,
					configuration: {},
					slot: null,
					isSlot: false
				});
				await contentBlock.component_appendOuterChild(itemContent, contentBlock.component_DOM);
			}
		}
	};

	protected override async component_onVisibilityUpdateBeforeChilds(isVisible: boolean) {
		if (isVisible && this.currentSections.length === 0) {
			await this.openDefaultMaybe();
		}
	};

	async openMaybe(childId?: string) {
		if (childId) {
			return (await this.open(childId));
		}
		return false;
	};

	override async open(childrenIds: string | string[]) {
		childrenIds = Array.isArray(childrenIds) ? childrenIds : [childrenIds];

		const closingChildrenIds = this.currentSections
			.filter(section => !childrenIds.includes(section.id))
			.map(section => section.id);

		if (closingChildrenIds.length > 0) {
			const closable = await this.close(closingChildrenIds);
			if (!closable) return false;
		}

		const proceed = await super.open(childrenIds);
		if (!proceed) return false;

		for (const childId of childrenIds) {
			const view = this.getView(childId);
			this.currentSections.push(view);
			this.getViewHeader(childId).component_DOM.classList.add('opened');
			this.getViewContent(childId).component_DOM.classList.add('opened');
		}

		await this.component_dispatchVisibilityUpdateOnChilds();

		await this.dispatchEvent('menu-open', {
			details: {
				sections: this.currentSections
			}
		});

		return true;
	};

	async openDefaultMaybe() {
		if (this.component_configuration.defaultSection) {
			await this.open(this.component_configuration.defaultSection);
		}
	};

	override async close(childrenIds: string | string[]) {
		childrenIds = Array.isArray(childrenIds) ? childrenIds : [childrenIds];

		if (childrenIds.length === 0 || this.currentSections.length === 0) {
			return false;
		}

		const proceed = await super.close(childrenIds);
		if (!proceed) return false;

		const closedSections: SectionComponent[] = [];

		this.currentSections = this.currentSections
			.filter(section => {
				if (childrenIds.includes(section.id)) {
					closedSections.push(section);
					section.component_DOM.classList.remove('opened');
					return false;
				}
				return true;
			});

		for (const child of closedSections) {
			this.getViewHeader(child.id).component_DOM.classList.remove('opened');
			this.getViewContent(child.id).component_DOM.classList.remove('opened');
		}

		await this.component_dispatchVisibilityUpdateOnChilds();

		await this.dispatchEvent('menu-close', {
			details: {
				newSections: this.currentSections,
				oldSections: closedSections
			}
		});

		return true;
	};

	private async onInputButton(event: STD.events.Event<any, CM.Component<any, any>>) {
		if (!(event.target instanceof CM.Component)) {
			throw STD.errors.user('Event target is not a component!', {event});
		}
		/**
		 * There may be buttons that are not bound to any view, so they must not be accounted for.
		 **/
		if (event.target.id !== 'header') {
			return;
		}
		event.stopPropagation();
		if (!event.target.component_parent) {
			/** There is a bug somewhere! **/
			this.component_throwLibraryError('', {});
		}
		await this.open(event.target.component_parent.id);
	};

	// protected override component_childIsVisible(
	// 	child: CM.DOMComponent<any, any, any>
	// ) {
	// 	if (!this.currentSection) {
	// 		return (child.component_isActive());
	// 	}
	// 	return (
	// 		(this.currentSection === child) &&
	// 		child.component_isActive()
	// 	);
	// };
	//
	// override component_childIsActive(child: CM.Component<any, any>) {
	// 	if (this.currentSection) {
	// 		if (this.currentSection !== child) {
	// 			return (false);
	// 		}
	// 	}
	// 	return (super.component_childIsActive(child));
	// };

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IMenuTabComponentConfiguration
	) {
		super(descriptor, configuration);
		/** Referenced by event listener in json configuration **/
		void this.onInputButton;
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({});
	};
};
export namespace NS {
	export const Component = MenuTabComponent;
	export type Component = MenuTabComponent;
	export type Configuration = IMenuTabComponentConfiguration;
}
import './MenuTab.scss';
import {
	MenuViewsComponent,
	IMenuViewsComponentConfiguration
}                           from '../MenuViews/MenuViews';
import { SectionComponent } from '../../Section/Section';
import manifestXMLSource    from './MenuTab.xml';
import { BlockComponent }   from '../../Block/Block';

CM.Manager.declare({
	id: 'menu-tab',
	type: MenuTabComponent,
	manifest: manifestXMLSource
});
